import React from 'react';
import { FiPlus, FiTrash2, FiLayers, FiType, FiImage, FiGrid, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { Button, Checkbox, Input, Select, Textarea } from 'components/common';
import ImageUploadField from 'components/admin/ImageUploadField';
import IconEmojiPicker from './IconEmojiPicker';

const ITEM_TYPES = [
    { value: 'text', label: '📝 Chỉ Văn bản (Only Text)', icon: <FiType /> },
    { value: 'image', label: '🖼️ Chỉ Hình ảnh (Only Image)', icon: <FiImage /> },
    { value: 'text_image', label: '🖼️📝 Văn bản + Hình ảnh (Text & Image)', icon: <FiLayers /> },
    { value: 'card', label: '💡 Thẻ nổi bật / Tính năng (Card)', icon: <FiGrid /> },
];

const LAYOUT_OPTIONS = [
    { value: 'left', label: '🖼️👈 Ảnh bên trái — Chữ bên phải' },
    { value: 'right', label: '👉🖼️ Chữ bên trái — Ảnh bên phải' },
    { value: 'top', label: '👆🖼️ Ảnh ở trên — Chữ ở dưới' },
];

export const createNewSection = (languages = [], sortOrder = 0) => ({
    sortOrder,
    isActive: true,
    translation: languages.map(lang => ({
        languageId: lang.id,
        title: '',
        subtitle: '',
        badge: '',
        description: '',
    })),
    items: [],
});

export const createNewItem = (languages = [], itemType = 'text', sortOrder = 0) => ({
    itemType,
    layout: itemType === 'text_image' ? 'left' : 'full',
    imageUrl: '',
    sortOrder,
    isActive: true,
    translation: languages.map(lang => ({
        languageId: lang.id,
        title: '',
        content: '',
        caption: '',
        badge: '',
    })),
});

const ProductSolutionSectionsEditor = ({
    sections = [],
    languages = [],
    activeTab = 0,
    onChange,
}) => {
    const currentLang = languages[activeTab] || languages[0];

    const handleAddSection = () => {
        const nextOrder = sections.length > 0 ? Math.max(...sections.map(s => Number(s.sortOrder) || 0)) + 1 : 0;
        const newSec = createNewSection(languages, nextOrder);
        // Default with 1 item for convenience
        newSec.items.push(createNewItem(languages, 'text_image', 0));
        onChange([...sections, newSec]);
    };

    const handleRemoveSection = (sectionIndex) => {
        onChange(sections.filter((_, idx) => idx !== sectionIndex));
    };

    const handleSectionChange = (sectionIndex, field, value) => {
        const updated = sections.map((sec, idx) => {
            if (idx !== sectionIndex) return sec;
            return { ...sec, [field]: value };
        });
        onChange(updated);
    };

    const handleSectionTranslationChange = (sectionIndex, langId, field, value) => {
        const updated = sections.map((sec, idx) => {
            if (idx !== sectionIndex) return sec;
            const trans = (sec.translation || []).map(t => {
                if (Number(t.languageId) === Number(langId)) {
                    return { ...t, [field]: value };
                }
                return t;
            });
            // If translation object for this lang didn't exist, append it
            if (!trans.some(t => Number(t.languageId) === Number(langId))) {
                trans.push({ languageId: langId, [field]: value });
            }
            return { ...sec, translation: trans };
        });
        onChange(updated);
    };

    const handleMoveSection = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= sections.length) return;
        const updated = [...sections];
        const temp = updated[index];
        updated[index] = updated[targetIndex];
        updated[targetIndex] = temp;
        // update sortOrder
        updated.forEach((s, i) => { s.sortOrder = i; });
        onChange(updated);
    };

    // --- Items operations inside a Section ---
    const handleAddItem = (sectionIndex, itemType = 'text') => {
        const targetSec = sections[sectionIndex];
        const items = targetSec.items || [];
        const nextOrder = items.length > 0 ? Math.max(...items.map(it => Number(it.sortOrder) || 0)) + 1 : 0;
        const newItem = createNewItem(languages, itemType, nextOrder);

        const updated = sections.map((sec, idx) => {
            if (idx !== sectionIndex) return sec;
            return { ...sec, items: [...(sec.items || []), newItem] };
        });
        onChange(updated);
    };

    const handleRemoveItem = (sectionIndex, itemIndex) => {
        const updated = sections.map((sec, idx) => {
            if (idx !== sectionIndex) return sec;
            return { ...sec, items: (sec.items || []).filter((_, iIdx) => iIdx !== itemIndex) };
        });
        onChange(updated);
    };

    const handleItemChange = (sectionIndex, itemIndex, field, value) => {
        const updated = sections.map((sec, idx) => {
            if (idx !== sectionIndex) return sec;
            const updatedItems = (sec.items || []).map((it, iIdx) => {
                if (iIdx !== itemIndex) return it;
                return { ...it, [field]: value };
            });
            return { ...sec, items: updatedItems };
        });
        onChange(updated);
    };

    const handleItemTranslationChange = (sectionIndex, itemIndex, langId, field, value) => {
        const updated = sections.map((sec, idx) => {
            if (idx !== sectionIndex) return sec;
            const updatedItems = (sec.items || []).map((it, iIdx) => {
                if (iIdx !== itemIndex) return it;
                const trans = (it.translation || []).map(t => {
                    if (Number(t.languageId) === Number(langId)) {
                        return { ...t, [field]: value };
                    }
                    return t;
                });
                if (!trans.some(t => Number(t.languageId) === Number(langId))) {
                    trans.push({ languageId: langId, [field]: value });
                }
                return { ...it, translation: trans };
            });
            return { ...sec, items: updatedItems };
        });
        onChange(updated);
    };

    return (
        <div className="ps-sections-editor">
            <div className="ps-sections-editor__header">
                <div>
                    <h3 className="ps-sections-editor__title">
                        Các Khối Phân Đoạn &amp; Trường Thông Tin (Structured Sections)
                    </h3>
                    <p className="ps-sections-editor__desc">
                        Tạo các khối phân đoạn theo chủ đề (Tổng quan, Tính năng, Demo, Lợi ích...) và thêm nhiều trường thông tin bên trong. Đang chỉnh sửa theo ngôn ngữ: <strong>{currentLang?.name}</strong>.
                    </p>
                </div>
                <Button type="button" variant="primary" onClick={handleAddSection}>
                    <FiPlus size={16} /> Thêm Phân Đoạn Mới
                </Button>
            </div>

            {sections.length === 0 ? (
                <div className="ps-sections-editor__empty">
                    <p>Chưa có phân đoạn nào.</p>
                    <span>Nhấn "Thêm Phân Đoạn Mới" để bắt đầu tạo nội dung có cấu trúc rõ ràng cho sản phẩm.</span>
                    <Button type="button" variant="secondary" onClick={handleAddSection} style={{ marginTop: 12 }}>
                        <FiPlus size={14} /> Thêm Phân Đoạn Đầu Tiên
                    </Button>
                </div>
            ) : (
                <div className="ps-sections-list">
                    {sections.map((section, sIdx) => {
                        const secTranslation = (section.translation || []).find(
                            t => Number(t.languageId) === Number(currentLang?.id)
                        ) || {};

                        return (
                            <div className="ps-section-card" key={section.id || sIdx}>
                                {/* Section Top Bar */}
                                <div className="ps-section-card__top">
                                    <div className="ps-section-card__identity">
                                        <span className="ps-section-card__badge">Phân đoạn #{sIdx + 1}</span>
                                        <strong className="ps-section-card__name">
                                            {secTranslation.title || `(Chưa đặt tiêu đề phân đoạn #${sIdx + 1})`}
                                        </strong>
                                    </div>
                                    <div className="ps-section-card__actions">
                                        <button
                                            type="button"
                                            className="icon-btn"
                                            title="Di chuyển lên"
                                            disabled={sIdx === 0}
                                            onClick={() => handleMoveSection(sIdx, -1)}
                                        >
                                            <FiArrowUp size={15} />
                                        </button>
                                        <button
                                            type="button"
                                            className="icon-btn"
                                            title="Di chuyển xuống"
                                            disabled={sIdx === sections.length - 1}
                                            onClick={() => handleMoveSection(sIdx, 1)}
                                        >
                                            <FiArrowDown size={15} />
                                        </button>
                                        <button
                                            type="button"
                                            className="icon-btn icon-btn--delete"
                                            title="Xóa phân đoạn"
                                            onClick={() => handleRemoveSection(sIdx)}
                                        >
                                            <FiTrash2 size={15} />
                                        </button>
                                    </div>
                                </div>

                                {/* Section Settings */}
                                <div className="ps-section-card__body">
                                    <div className="ps-section-settings-grid">
                                        <div className="form-row" style={{ gridTemplateColumns: '2fr 1fr' }}>
                                            <Input
                                                label={`Tiêu đề Phân đoạn (${currentLang?.name}) *`}
                                                placeholder="VD: Tổng quan Giải pháp / Tính năng nổi bật..."
                                                value={secTranslation.title || ''}
                                                onChange={(e) => handleSectionTranslationChange(sIdx, currentLang.id, 'title', e.target.value)}
                                                required
                                            />
                                            <Input
                                                label={`Huy hiệu / Tag (${currentLang?.name})`}
                                                placeholder="VD: NỔI BẬT, CÔNG NGHỆ, DEMO..."
                                                value={secTranslation.badge || ''}
                                                onChange={(e) => handleSectionTranslationChange(sIdx, currentLang.id, 'badge', e.target.value)}
                                            />
                                        </div>

                                        <Textarea
                                            label={`Mô tả ngắn / Phụ đề Phân đoạn (${currentLang?.name})`}
                                            placeholder="Nhập mô tả tóm tắt cho phân đoạn này nếu có..."
                                            value={secTranslation.subtitle || ''}
                                            onChange={(e) => handleSectionTranslationChange(sIdx, currentLang.id, 'subtitle', e.target.value)}
                                            rows={2}
                                        />

                                        <div className="ps-section-meta-row">
                                            <div style={{ width: 140 }}>
                                                <Input
                                                    label="Thứ tự sắp xếp"
                                                    type="number"
                                                    value={section.sortOrder ?? sIdx}
                                                    onChange={(e) => handleSectionChange(sIdx, 'sortOrder', Number(e.target.value))}
                                                />
                                            </div>
                                            <div style={{ paddingTop: 26 }}>
                                                <Checkbox
                                                    label="Hiển thị phân đoạn này"
                                                    checked={section.isActive}
                                                    onChange={(e) => handleSectionChange(sIdx, 'isActive', e.target.checked)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Items List Inside Section */}
                                    <div className="ps-items-container">
                                        <div className="ps-items-header">
                                            <h4>Các trường thông tin trong phân đoạn ({(section.items || []).length})</h4>
                                            <div className="ps-items-add-btns">
                                                <span style={{ fontSize: 13, color: '#666', marginRight: 4 }}>+ Thêm:</span>
                                                <button
                                                    type="button"
                                                    className="btn-add-item-type"
                                                    onClick={() => handleAddItem(sIdx, 'text_image')}
                                                    title="Thêm trường Text + Ảnh"
                                                >
                                                    🖼️📝 Văn bản + Ảnh
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-add-item-type"
                                                    onClick={() => handleAddItem(sIdx, 'text')}
                                                    title="Thêm trường Chỉ Văn bản"
                                                >
                                                    📝 Chỉ Văn bản
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-add-item-type"
                                                    onClick={() => handleAddItem(sIdx, 'image')}
                                                    title="Thêm trường Chỉ Hình ảnh"
                                                >
                                                    🖼️ Chỉ Hình ảnh
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-add-item-type"
                                                    onClick={() => handleAddItem(sIdx, 'card')}
                                                    title="Thêm thẻ tính năng"
                                                >
                                                    💡 Thẻ nổi bật
                                                </button>
                                            </div>
                                        </div>

                                        {(section.items || []).length === 0 ? (
                                            <div className="ps-items-empty">
                                                <span>Chưa có trường thông tin nào trong phân đoạn này. Hãy nhấn chọn một loại trường ở trên để thêm.</span>
                                            </div>
                                        ) : (
                                            <div className="ps-items-list">
                                                {(section.items || []).map((item, iIdx) => {
                                                    const itemTranslation = (item.translation || []).find(
                                                        t => Number(t.languageId) === Number(currentLang?.id)
                                                    ) || {};

                                                    return (
                                                        <div className={`ps-item-card ps-item-card--${item.itemType}`} key={item.id || iIdx}>
                                                            {/* Item Top Bar */}
                                                            <div className="ps-item-card__top">
                                                                <div className="ps-item-card__header-left">
                                                                    <span className="ps-item-card__number">{iIdx + 1}</span>
                                                                    <Select
                                                                        value={item.itemType}
                                                                        onChange={(e) => handleItemChange(sIdx, iIdx, 'itemType', e.target.value)}
                                                                        options={ITEM_TYPES}
                                                                        style={{ minWidth: 240, margin: 0 }}
                                                                    />
                                                                    {item.itemType === 'text_image' && (
                                                                        <Select
                                                                            value={item.layout || 'left'}
                                                                            onChange={(e) => handleItemChange(sIdx, iIdx, 'layout', e.target.value)}
                                                                            options={LAYOUT_OPTIONS}
                                                                            style={{ minWidth: 260, margin: 0 }}
                                                                        />
                                                                    )}
                                                                </div>

                                                                <button
                                                                    type="button"
                                                                    className="icon-btn icon-btn--delete"
                                                                    onClick={() => handleRemoveItem(sIdx, iIdx)}
                                                                    title="Xóa trường này"
                                                                >
                                                                    <FiTrash2 size={14} />
                                                                </button>
                                                            </div>

                                                            {/* Item Fields by Type */}
                                                            <div className="ps-item-card__fields">
                                                                {/* Only Text Mode */}
                                                                {item.itemType === 'text' && (
                                                                    <div className="ps-item-fields-group">
                                                                        <div className="form-row">
                                                                            <Input
                                                                                label={`Tiêu đề đoạn văn (${currentLang?.name})`}
                                                                                placeholder="VD: Kiến trúc hệ thống mở linh hoạt..."
                                                                                value={itemTranslation.title || ''}
                                                                                onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'title', e.target.value)}
                                                                            />
                                                                            <Input
                                                                                label={`Huy hiệu / Tag (${currentLang?.name})`}
                                                                                placeholder="VD: MODULE 1, BẢO MẬT..."
                                                                                value={itemTranslation.badge || ''}
                                                                                onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'badge', e.target.value)}
                                                                            />
                                                                        </div>
                                                                        <Textarea
                                                                            label={`Nội dung văn bản chi tiết (${currentLang?.name}) *`}
                                                                            placeholder="Nhập nội dung mô tả, tính năng hoặc danh sách bullet..."
                                                                            value={itemTranslation.content || ''}
                                                                            onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'content', e.target.value)}
                                                                            rows={4}
                                                                            required
                                                                        />
                                                                    </div>
                                                                )}

                                                                {/* Only Image Mode */}
                                                                {item.itemType === 'image' && (
                                                                    <div className="ps-item-fields-group">
                                                                        <ImageUploadField
                                                                            label="Hình ảnh trưng bày / Demo *"
                                                                            value={item.imageUrl || ''}
                                                                            folder="product-solutions/contents"
                                                                            onChange={(url) => handleItemChange(sIdx, iIdx, 'imageUrl', url)}
                                                                        />
                                                                        <div className="form-row" style={{ marginTop: 10 }}>
                                                                            <Input
                                                                                label={`Chú thích ảnh (${currentLang?.name})`}
                                                                                placeholder="VD: Giao diện bảng điều khiển trung tâm HIS"
                                                                                value={itemTranslation.caption || ''}
                                                                                onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'caption', e.target.value)}
                                                                            />
                                                                            <Input
                                                                                label={`Tiêu đề ảnh / Alt Text (${currentLang?.name})`}
                                                                                placeholder="VD: Dashboard Quản trị Bệnh viện"
                                                                                value={itemTranslation.title || ''}
                                                                                onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'title', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Text + Image Mode */}
                                                                {item.itemType === 'text_image' && (
                                                                    <div className="ps-item-fields-group ps-item-fields-group--split">
                                                                        <div className="ps-split-left">
                                                                            <div className="form-row">
                                                                                <Input
                                                                                    label={`Tiêu đề (${currentLang?.name})`}
                                                                                    placeholder="VD: Phân hệ Quản lý Bệnh nhân Ngoại trú"
                                                                                    value={itemTranslation.title || ''}
                                                                                    onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'title', e.target.value)}
                                                                                />
                                                                                <Input
                                                                                    label={`Huy hiệu / Tag (${currentLang?.name})`}
                                                                                    placeholder="VD: CHỨC NĂNG CHÍNH"
                                                                                    value={itemTranslation.badge || ''}
                                                                                    onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'badge', e.target.value)}
                                                                                />
                                                                            </div>
                                                                            <Textarea
                                                                                label={`Nội dung chi tiết (${currentLang?.name}) *`}
                                                                                placeholder="Nhập phần giải thích, thuyết minh chi tiết..."
                                                                                value={itemTranslation.content || ''}
                                                                                onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'content', e.target.value)}
                                                                                rows={4}
                                                                                required
                                                                            />
                                                                        </div>
                                                                        <div className="ps-split-right">
                                                                            <ImageUploadField
                                                                                label="Hình ảnh minh họa đi kèm *"
                                                                                value={item.imageUrl || ''}
                                                                                folder="product-solutions/contents"
                                                                                onChange={(url) => handleItemChange(sIdx, iIdx, 'imageUrl', url)}
                                                                            />
                                                                            <Input
                                                                                label={`Chú thích ảnh (${currentLang?.name})`}
                                                                                placeholder="VD: Ảnh chụp màn hình tiếp nhận bệnh nhân"
                                                                                value={itemTranslation.caption || ''}
                                                                                onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'caption', e.target.value)}
                                                                                style={{ marginTop: 8 }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Card / Highlight Mode */}
                                                                {item.itemType === 'card' && (
                                                                    <div className="ps-item-fields-group">
                                                                        <div className="form-row">
                                                                            <IconEmojiPicker
                                                                                label={`Icon Emoji / Huy hiệu (${currentLang?.name})`}
                                                                                placeholder="VD: FiZap, ⚡, 🛡️, hoặc 'TỐI ƯU'"
                                                                                value={itemTranslation.badge || ''}
                                                                                onChange={(val) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'badge', val)}
                                                                            />
                                                                            <Input
                                                                                label={`Tiêu đề thẻ (${currentLang?.name}) *`}
                                                                                placeholder="VD: Tích hợp bệnh án điện tử EMR"
                                                                                value={itemTranslation.title || ''}
                                                                                onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'title', e.target.value)}
                                                                                required
                                                                            />
                                                                        </div>
                                                                        <Textarea
                                                                            label={`Nội dung thẻ (${currentLang?.name})`}
                                                                            placeholder="Mô tả ngắn gọn điểm nổi bật..."
                                                                            value={itemTranslation.content || ''}
                                                                            onChange={(e) => handleItemTranslationChange(sIdx, iIdx, currentLang.id, 'content', e.target.value)}
                                                                            rows={2}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ProductSolutionSectionsEditor;
